import './ActivitySummaryCard.css'

const SummaryItem = ({ icon, colorClass, label, value, tooltip }) => (
  <div className="d-flex align-items-center" title={tooltip}>
    <i className={`bx ${icon} ${colorClass} fs-3 me-2`}></i>
    <div className="text-nowrap">
      <small className="text-muted">{label}</small>
      <div className="fw-semibold">{value}</div>
    </div>
  </div>
)

const ActivitySummaryCard = ({
  total = 0,
  movement = 0,
  fuelRefill = 0,
  totalDistance = 0,
  totalFuel = 0,
  efficiency = 0,
  avgRunPerRefuel = 0,
  avgEfficiencyPerRefuel = 0,
  estimatedMaxRange = 0,
  distanceSinceLastRefuel = 0,
  remainingMileageBeforeRefill = 0,
  estimatedFuelLeft = 0,
  needsRefill = false,
  predictionMethod = "unknown",
  fuelEstimationMethod = "unknown",
  consumptionPatterns = 0,
  predictedNextRefuelMileage = null,
  predictedNextRefuelDate = null,
  refillThreshold = 0,
  confidence = "low",
  vehicleType = "unknown",
  drivingPattern = "mixed",
  vehicleProfile = ""
}) => {
  
  const getPredictionMethodText = (method) => {
    switch(method) {
      case "historical_pattern": return "Historical Patterns";
      case "efficiency_based": return "Fuel Efficiency";
      case "movement_pattern": return "Movement Patterns";
      case "toyota_profile": return "Toyota Profile";
      default: return "Unknown";
    }
  }

  const getFuelEstimationText = (method) => {
    switch(method) {
      case "pattern_efficiency": return "Pattern Efficiency";
      case "overall_efficiency": return "Overall Efficiency";
      case "consumption_based": return "Consumption Based";
      case "historical_pattern": return "Historical Pattern";
      case "toyota_profile": return "Toyota Profile";
      default: return "Unknown";
    }
  }

  const getConfidenceColor = (level) => {
    switch(level) {
      case "high": return "text-success";
      case "medium": return "text-warning";
      case "low": return "text-secondary";
      default: return "text-secondary";
    }
  }

  const getVehicleTypeText = (type) => {
    switch(type) {
      case "hybrid": return "Toyota Hybrid";
      case "nonHybrid": return "Toyota Sedan";
      case "suv": return "Toyota SUV";
      default: return "Unknown Vehicle";
    }
  }

  const getDrivingPatternText = (pattern) => {
    return pattern.charAt(0).toUpperCase() + pattern.slice(1) + " Driving";
  }

  // ALL STATISTICS - NO INFORMATION SKIPPED
  const basicStats = [
    {
      icon: "bx-car",
      colorClass: "text-success",
      label: "Total Movements",
      value: `${movement} times`,
      tooltip: "Total number of vehicle movements recorded"
    },
    {
      icon: "bx-gas-pump",
      colorClass: "text-warning",
      label: "Fuel Refills",
      value: `${fuelRefill} times`,
      tooltip: "Total number of fuel refill events recorded"
    },
    {
      icon: "bx-map",
      colorClass: "text-primary",
      label: "Total Distance",
      value: `${parseFloat(totalDistance).toLocaleString()} km`,
      tooltip: "Total distance covered based on movement data"
    },
    {
      icon: "bx-droplet",
      colorClass: "text-info",
      label: "Total Fuel Consumed",
      value: `${parseFloat(totalFuel).toFixed(1)} L`,
      tooltip: "Total fuel consumed from all refill events"
    }
  ]

  const efficiencyStats = [
    {
      icon: "bx-bar-chart-alt-2",
      colorClass: "text-info",
      label: "Overall Fuel Efficiency",
      value: `${parseFloat(efficiency).toFixed(1)} km/L`,
      tooltip: "Overall efficiency calculated from total distance ÷ total fuel"
    },
    {
      icon: "bx-line-chart",
      colorClass: "text-success",
      label: "Pattern Efficiency",
      value: `${parseFloat(avgEfficiencyPerRefuel).toFixed(1)} km/L`,
      tooltip: "Average fuel efficiency between refuel cycles"
    },
    {
      icon: "bx-trending-up",
      colorClass: "text-primary",
      label: "Average Run per Refuel",
      value: `${parseFloat(avgRunPerRefuel).toFixed(0)} km`,
      tooltip: "Average distance covered between consecutive refuels"
    },
    {
      icon: "bx-target-lock",
      colorClass: "text-dark",
      label: "Estimated Max Range",
      value: `${parseFloat(estimatedMaxRange).toFixed(0)} km`,
      tooltip: "Estimated maximum distance achievable per refuel cycle"
    }
  ]

  const currentStatus = [
    {
      icon: "bx-navigation",
      colorClass: "text-info",
      label: "Distance Since Last Refuel",
      value: `${parseFloat(distanceSinceLastRefuel).toFixed(0)} km`,
      tooltip: "Distance traveled since the most recent fuel refill"
    },
    {
      icon: "bx-tachometer",
      colorClass: needsRefill ? "text-warning" : "text-success",
      label: "Remaining Range",
      value: `${parseFloat(remainingMileageBeforeRefill).toFixed(0)} km`,
      tooltip: "Estimated distance that can be covered before next refuel is needed"
    },
    {
      icon: "bx-droplet",
      colorClass: needsRefill ? "text-warning" : "text-info",
      label: "Estimated Fuel Left",
      value: `${parseFloat(estimatedFuelLeft).toFixed(1)} L`,
      tooltip: "Estimated remaining fuel based on consumption patterns"
    },
    {
      icon: "bx-line-chart-down",
      colorClass: "text-dark",
      label: "Refill Threshold",
      value: `${parseFloat(refillThreshold).toFixed(0)} km`,
      tooltip: "Distance threshold that triggers refill recommendation"
    }
  ]

  const refuelPredictions = [
    {
      icon: needsRefill ? "bx-error-circle" : "bx-check-circle",
      colorClass: needsRefill ? "text-danger" : "text-success",
      label: "Refill Status",
      value: needsRefill ? "REFUEL SOON" : "FUEL OK",
      tooltip: needsRefill ? "Vehicle needs refueling soon" : "Fuel level is sufficient"
    },
    {
      icon: "bx-trending-up",
      colorClass: "text-success",
      label: "Next Refuel Mileage",
      value: predictedNextRefuelMileage ? `${parseInt(predictedNextRefuelMileage).toLocaleString()} km` : "Calculating...",
      tooltip: "Predicted odometer reading when next refuel will be needed"
    },
    {
      icon: "bx-calendar-event",
      colorClass: "text-primary",
      label: "Next Refuel Date",
      value: predictedNextRefuelDate || "Calculating...",
      tooltip: "Estimated date when next refuel will be needed based on usage patterns"
    },
    {
      icon: "bx-data",
      colorClass: "text-info",
      label: "Consumption Patterns",
      value: `${consumptionPatterns} patterns analyzed`,
      tooltip: "Number of refuel cycles used for consumption pattern analysis"
    }
  ]

  const vehicleAnalysis = [
    {
      icon: "bx-cog",
      colorClass: "text-primary",
      label: "Vehicle Type",
      value: getVehicleTypeText(vehicleType),
      tooltip: "Estimated vehicle type based on consumption patterns and refuel behavior"
    },
    {
      icon: "bx-map",
      colorClass: "text-info",
      label: "Driving Pattern",
      value: getDrivingPatternText(drivingPattern),
      tooltip: "Analyzed driving pattern based on movement types and routes"
    },
    {
      icon: "bx-user-voice",
      colorClass: "text-secondary",
      label: "Vehicle Profile",
      value: vehicleProfile || `${vehicleType} (${drivingPattern})`,
      tooltip: "Combined vehicle type and driving pattern analysis"
    }
  ]

  const analyticsMethods = [
    {
      icon: "bx-brain",
      colorClass: "text-purple",
      label: "Prediction Method",
      value: getPredictionMethodText(predictionMethod),
      tooltip: "Primary algorithm used for fuel consumption predictions"
    },
    {
      icon: "bx-calculator",
      colorClass: "text-purple",
      label: "Fuel Estimation Method",
      value: getFuelEstimationText(fuelEstimationMethod),
      tooltip: "Method used to calculate estimated remaining fuel"
    },
    {
      icon: "bx-bulb",
      colorClass: getConfidenceColor(confidence),
      label: "Prediction Confidence",
      value: `${confidence.charAt(0).toUpperCase() + confidence.slice(1)} Confidence`,
      tooltip: "Reliability level of predictions based on available data quality and quantity"
    },
    {
      icon: "bx-chip",
      colorClass: "text-success",
      label: "Analysis Model",
      value: "Toyota Smart Fuel AI",
      tooltip: "Advanced Toyota-specific fuel consumption analysis model"
    }
  ]

  return (
    <div className="bg-light rounded shadow-sm border">
      {/* Basic Statistics Section */}
      <div className="p-3 border-bottom">
        <h6 className="fw-bold mb-3 text-primary">
          <i className="bx bx-stats me-2"></i>
          Basic Statistics
        </h6>
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          {basicStats.map((item, idx) => (
            <SummaryItem key={`basic-${idx}`} {...item} />
          ))}
        </div>
      </div>

      {/* Vehicle Analysis Section */}
      <div className="p-3 border-bottom">
        <h6 className="fw-bold mb-3 text-info">
          <i className="bx bx-car me-2"></i>
          Toyota Vehicle Analysis
        </h6>
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          {vehicleAnalysis.map((item, idx) => (
            <SummaryItem key={`vehicle-${idx}`} {...item} />
          ))}
        </div>
      </div>

      {/* Efficiency Metrics Section */}
      <div className="p-3 border-bottom">
        <h6 className="fw-bold mb-3 text-warning">
          <i className="bx bx-line-chart me-2"></i>
          Efficiency Metrics
        </h6>
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          {efficiencyStats.map((item, idx) => (
            <SummaryItem key={`efficiency-${idx}`} {...item} />
          ))}
        </div>
      </div>

      {/* Current Status Section */}
      <div className="p-3 border-bottom">
        <h6 className="fw-bold mb-3 text-success">
          <i className="bx bx-current-location me-2"></i>
          Current Fuel Status
        </h6>
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          {currentStatus.map((item, idx) => (
            <SummaryItem key={`status-${idx}`} {...item} />
          ))}
        </div>
      </div>

      {/* Refuel Predictions Section */}
      <div className="p-3 border-bottom">
        <h6 className="fw-bold mb-3 text-danger">
          <i className="bx bx-gas-pump me-2"></i>
          Refuel Predictions
        </h6>
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          {refuelPredictions.map((item, idx) => (
            <SummaryItem key={`refuel-${idx}`} {...item} />
          ))}
        </div>
      </div>

      {/* Analytics & Methods Section */}
      <div className="p-3">
        <h6 className="fw-bold mb-3 text-secondary">
          <i className="bx bx-analytics me-2"></i>
          Analytics & Methods
        </h6>
        <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between">
          {analyticsMethods.map((item, idx) => (
            <SummaryItem key={`analytics-${idx}`} {...item} />
          ))}
        </div>
        
        {/* Comprehensive Status Alert */}
        <div className={`mt-3 p-3 rounded text-center ${needsRefill ? 'bg-warning bg-opacity-20 border-warning' : 'bg-success bg-opacity-20 border-success'} border`}>
          <div className="d-flex align-items-center justify-content-center">
            <i className={`bx ${needsRefill ? 'bx-error text-white' : 'bx-check-circle text-success'} me-2 fs-4`}></i>
            <div>
              <span className={`fw-semibold text-white`}>
                {needsRefill 
                  ? `⚠️ Refuel Recommended - Vehicle has exceeded ${parseFloat(refillThreshold).toFixed(0)}km threshold` 
                  : `✓ Fuel Status Normal - Next refuel expected in ${parseFloat(remainingMileageBeforeRefill).toFixed(0)}km`}
              </span>
              <div className="small text-muted mt-1">
                Based on {consumptionPatterns} consumption patterns with {confidence} confidence • 
                {predictedNextRefuelDate && ` Next refuel around ${predictedNextRefuelDate}`}
                {predictedNextRefuelMileage && ` at ${parseInt(predictedNextRefuelMileage).toLocaleString()}km`}
              </div>
              <div className="small text-muted">
                Vehicle: {getVehicleTypeText(vehicleType)} • Driving: {getDrivingPatternText(drivingPattern)} • 
                Method: {getPredictionMethodText(predictionMethod)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ActivitySummaryCard